import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { NominateBallot } from './NominateBallot';
import { AllocateBallot } from './AllocateBallot';
import { RevealPanel } from './RevealPanel';
import { ResultsTable } from './ResultsTable';
import {
  createRound,
  transitionRound,
  revealAllocateRound,
  getProgress,
  type CandidateHappening,
} from '@/lib/selections-api';
import type { SelectionRound, NominateRoundConfig, AllocateRoundConfig } from '@littlebooker/dal';

interface RoundsTabProps {
  selectionId: string;
  rounds: SelectionRound[];
  candidates: CandidateHappening[];
  recusedIds: Set<string>;
  isOwner: boolean;
  isCurrentUserVoter: boolean;
  suggestedMaxPicks: number;
  targetShortlist: number;
  targetFinal: number;
  voterCount: number;
  userName: (uid: string) => string;
  onChanged: () => void;
}

const STATE_COPY: Record<string, string> = {
  draft: 'Draft — not yet open',
  open: 'Open — voters can submit',
  closed: 'Closed — awaiting reveal',
  revealed: 'Revealed',
};

const inputClass = 'mt-1 w-full rounded-md border border-input bg-background px-3 py-2';

export function RoundsTab({
  selectionId,
  rounds,
  candidates,
  recusedIds,
  isOwner,
  isCurrentUserVoter,
  suggestedMaxPicks,
  targetShortlist,
  targetFinal,
  voterCount,
  userName,
  onChanged,
}: RoundsTabProps) {
  const [showCreate, setShowCreate] = useState(false);

  // Shortlist from the latest revealed nominate round — the fallback an allocate
  // round uses when its own snapshot is empty (e.g. created before reveal).
  const revealedShortlistIds =
    (rounds
      .filter((r) => r.kind === 'nominate' && r.state === 'revealed')
      .sort((a, b) => b.ordinal - a.ordinal)[0]?.config as { shortlist_happening_ids?: string[] })
      ?.shortlist_happening_ids ?? [];

  return (
    <div className="space-y-4 pt-4">
      {isOwner && (
        <div>
          {showCreate ? (
            <CreateRoundForm
              selectionId={selectionId}
              nextOrdinal={rounds.length + 1}
              suggestedMaxPicks={suggestedMaxPicks}
              onDone={() => {
                setShowCreate(false);
                onChanged();
              }}
              onCancel={() => setShowCreate(false)}
            />
          ) : (
            <Button size="sm" onClick={() => setShowCreate(true)}>
              Add round
            </Button>
          )}
        </div>
      )}

      {rounds.length === 0 ? (
        <p className="text-sm text-muted-foreground">No rounds yet.</p>
      ) : (
        rounds.map((round) => (
          <RoundCard
            key={round.id}
            selectionId={selectionId}
            round={round}
            candidates={candidates}
            recusedIds={recusedIds}
            isOwner={isOwner}
            isCurrentUserVoter={isCurrentUserVoter}
            targetShortlist={targetShortlist}
            targetFinal={targetFinal}
            voterCount={voterCount}
            revealedShortlistIds={revealedShortlistIds}
            userName={userName}
            onChanged={onChanged}
          />
        ))
      )}
    </div>
  );
}

function CreateRoundForm({
  selectionId,
  nextOrdinal,
  suggestedMaxPicks,
  onDone,
  onCancel,
}: {
  selectionId: string;
  nextOrdinal: number;
  suggestedMaxPicks: number;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [kind, setKind] = useState<'nominate' | 'allocate'>(nextOrdinal === 1 ? 'nominate' : 'allocate');
  // nominate config
  const [maxPicks, setMaxPicks] = useState(String(suggestedMaxPicks || 5));
  const [wildcards, setWildcards] = useState('2');
  const [requireNote, setRequireNote] = useState(false);
  // allocate config
  const [budget, setBudget] = useState('100');
  const [perItemCap, setPerItemCap] = useState('30');
  const [error, setError] = useState('');

  const create = useMutation({
    mutationFn: () => {
      const config =
        kind === 'nominate'
          ? { max_picks: Number(maxPicks), wildcards: Number(wildcards), require_note: requireNote }
          : { budget: Number(budget), per_item_cap: Number(perItemCap) };
      return createRound(selectionId, { kind, config });
    },
    onSuccess: onDone,
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'Failed to create round'),
  });

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">New round (#{nextOrdinal})</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <label className="text-xs font-medium">Kind</label>
          <select className={inputClass} value={kind} onChange={(e) => setKind(e.target.value as 'nominate' | 'allocate')}>
            <option value="nominate">Nominate (produces a shortlist)</option>
            <option value="allocate">Allocate (100 points across the shortlist)</option>
          </select>
        </div>

        {kind === 'nominate' ? (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium">Max picks / voter</label>
              <input className={inputClass} type="number" min={1} value={maxPicks} onChange={(e) => setMaxPicks(e.target.value)} />
              <p className="mt-1 text-xs text-muted-foreground">Suggested {suggestedMaxPicks} from your plan.</p>
            </div>
            <div>
              <label className="text-xs font-medium">Wildcards / voter</label>
              <input className={inputClass} type="number" min={0} value={wildcards} onChange={(e) => setWildcards(e.target.value)} />
            </div>
            <label className="col-span-2 flex items-center gap-2 text-sm">
              <input type="checkbox" checked={requireNote} onChange={(e) => setRequireNote(e.target.checked)} />
              Require a note on every nomination
            </label>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium">Budget (points)</label>
              <input className={inputClass} type="number" min={1} value={budget} onChange={(e) => setBudget(e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-medium">Per-item cap</label>
              <input className={inputClass} type="number" min={1} value={perItemCap} onChange={(e) => setPerItemCap(e.target.value)} />
            </div>
          </div>
        )}

        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button size="sm" onClick={() => create.mutate()} disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create round'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function RoundCard({
  selectionId,
  round,
  candidates,
  recusedIds,
  isOwner,
  isCurrentUserVoter,
  targetShortlist,
  targetFinal,
  voterCount,
  revealedShortlistIds,
  userName,
  onChanged,
}: {
  selectionId: string;
  round: SelectionRound;
  candidates: CandidateHappening[];
  recusedIds: Set<string>;
  isOwner: boolean;
  isCurrentUserVoter: boolean;
  targetShortlist: number;
  targetFinal: number;
  voterCount: number;
  revealedShortlistIds: string[];
  userName: (uid: string) => string;
  onChanged: () => void;
}) {
  const [showBallot, setShowBallot] = useState(false);
  const [showRecut, setShowRecut] = useState(false);
  const [confirmingReveal, setConfirmingReveal] = useState(false);
  const shortlistIds =
    (round.config as { shortlist_happening_ids?: string[] }).shortlist_happening_ids ?? [];
  // Allocate rounds vote over their own snapshot, or the revealed round's
  // shortlist when the snapshot is empty (created before reveal).
  const effectiveShortlist = shortlistIds.length > 0 ? shortlistIds : revealedShortlistIds;

  const transition = useMutation({
    mutationFn: (to: 'open' | 'closed') => transitionRound(selectionId, round.id, to),
    onSuccess: onChanged,
  });
  const revealAllocate = useMutation({
    mutationFn: () => revealAllocateRound(selectionId, round.id),
    onSuccess: () => {
      setConfirmingReveal(false);
      onChanged();
    },
  });

  const configSummary =
    round.kind === 'nominate'
      ? (() => {
          const c = round.config as NominateRoundConfig;
          return `max ${c.max_picks ?? '?'} picks · ${c.wildcards ?? 0} wildcards${c.require_note ? ' · notes required' : ''}`;
        })()
      : (() => {
          const c = round.config as AllocateRoundConfig;
          return `${c.budget ?? '?'} points · cap ${c.per_item_cap ?? '?'}`;
        })();

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base capitalize">
            Round {round.ordinal}: {round.kind}
          </CardTitle>
          <span className="rounded-full border px-2 py-0.5 text-xs capitalize text-muted-foreground">
            {round.state}
          </span>
        </div>
        <CardDescription>
          {STATE_COPY[round.state]} · {configSummary}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {isOwner && (round.state === 'draft' || round.state === 'open') && (
          <div className="flex flex-wrap gap-2">
            {round.state === 'draft' && (
              <Button size="sm" onClick={() => transition.mutate('open')} disabled={transition.isPending}>
                Open round
              </Button>
            )}
            {round.state === 'open' && (
              <Button size="sm" variant="outline" onClick={() => transition.mutate('closed')} disabled={transition.isPending}>
                Close round
              </Button>
            )}
          </div>
        )}

        {isOwner && round.kind === 'nominate' && round.state === 'closed' && (
          <RevealPanel
            selectionId={selectionId}
            roundId={round.id}
            targetShortlist={targetShortlist}
            targetFinal={targetFinal}
            expectedVoters={voterCount}
            mode="reveal"
            onRevealed={onChanged}
          />
        )}

        {round.kind === 'nominate' && round.state === 'revealed' && (
          <div className="space-y-2">
            <div>
              <p className="text-sm font-medium">Shortlist ({shortlistIds.length})</p>
              <ul className="mt-1 list-inside list-disc text-sm text-muted-foreground">
                {shortlistIds.map((hid) => (
                  <li key={hid}>{candidates.find((c) => c.id === hid)?.title ?? hid}</li>
                ))}
              </ul>
            </div>
            {isOwner && (
              <div>
                <Button variant="outline" size="sm" onClick={() => setShowRecut((s) => !s)}>
                  <ChevronDown
                    className={`mr-1 h-4 w-4 transition-transform ${showRecut ? '' : '-rotate-90'}`}
                  />
                  {showRecut ? 'Hide re-cut' : 'Re-cut shortlist'}
                </Button>
                {showRecut && (
                  <div className="mt-2">
                    <p className="mb-2 text-xs text-muted-foreground">
                      Re-runs the cutoff on the same (already-revealed) ballots. Note: an allocate round
                      snapshots the shortlist when created, so re-cutting won't change a round-2 already in
                      progress — recreate round 2 to adopt the new shortlist.
                    </p>
                    <RevealPanel
                      selectionId={selectionId}
                      roundId={round.id}
                      targetShortlist={targetShortlist}
                      targetFinal={targetFinal}
                      expectedVoters={voterCount}
                      mode="recut"
                      onRevealed={() => {
                        setShowRecut(false);
                        onChanged();
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {isOwner && round.kind === 'allocate' && round.state === 'closed' && (
          <div className="rounded-md border p-3">
            {confirmingReveal ? (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-destructive">
                  Reveal discloses all ballots and can't be undone.
                </span>
                <Button size="sm" onClick={() => revealAllocate.mutate()} disabled={revealAllocate.isPending}>
                  {revealAllocate.isPending ? 'Revealing…' : 'Confirm reveal'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setConfirmingReveal(false)}>
                  Cancel
                </Button>
              </div>
            ) : (
              <Button size="sm" onClick={() => setConfirmingReveal(true)}>
                Reveal results
              </Button>
            )}
          </div>
        )}

        {round.kind === 'allocate' && round.state === 'revealed' && (
          <ResultsTable
            selectionId={selectionId}
            roundId={round.id}
            candidates={candidates}
            targetFinal={targetFinal}
          />
        )}

        {isOwner && round.state !== 'draft' && <OwnerProgress selectionId={selectionId} roundId={round.id} userName={userName} />}

        {isCurrentUserVoter && round.state !== 'draft' && (
          <div>
            <Button variant="outline" size="sm" onClick={() => setShowBallot((s) => !s)}>
              <ChevronDown
                className={`mr-1 h-4 w-4 transition-transform ${showBallot ? '' : '-rotate-90'}`}
              />
              {showBallot ? 'Hide my ballot' : 'Your ballot'}
            </Button>
            {showBallot && (
              <div className="mt-2">
                {round.kind === 'nominate' ? (
                  <NominateBallot
                    selectionId={selectionId}
                    roundId={round.id}
                    candidates={candidates}
                    recusedIds={recusedIds}
                    config={round.config as NominateRoundConfig}
                    editable={round.state === 'open'}
                  />
                ) : (
                  <AllocateBallot
                    selectionId={selectionId}
                    roundId={round.id}
                    candidates={candidates}
                    recusedIds={recusedIds}
                    config={round.config as AllocateRoundConfig}
                    shortlistIds={effectiveShortlist}
                    editable={round.state === 'open'}
                  />
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function OwnerProgress({
  selectionId,
  roundId,
  userName,
}: {
  selectionId: string;
  roundId: string;
  userName: (uid: string) => string;
}) {
  const { data: progress } = useQuery({
    queryKey: ['selection-progress', roundId],
    queryFn: () => getProgress(selectionId, roundId),
    refetchInterval: 15000,
  });

  if (!progress) return null;
  return (
    <div className="rounded-md border bg-muted/30 p-3 text-sm">
      <p className="font-medium">
        {progress.submitted} / {progress.expected} ballots submitted
      </p>
      {progress.submittedVoterIds.length > 0 && (
        <p className="text-muted-foreground">
          Submitted: {progress.submittedVoterIds.map(userName).join(', ')}
        </p>
      )}
      <p className="mt-1 text-xs text-muted-foreground">
        Counts only — ballot contents stay hidden until reveal.
      </p>
    </div>
  );
}
